import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";

import { QUERY_KEYS } from "@/lib/react-query/queryKeys";
import {
  getCurrentUser,
  signOutAccount,
  getUsers,
  createPost,
  getPostById,
  updatePost,
  deletePost,
  likePost,
  getUserById,
  updateUser,
  getRecentPosts,
  getInfinitePosts,
  searchPosts,
  savePost,
  deleteSavedPost,
  fetchMessages,
  sendMessage,
  fetchMessagesForUser,
  fetchUsersByIds,
  getLastMessages,
  deleteAllSessions,
  getFollowingCount,
  getFollowersCount,
  checkFollowStatus,
  bulkCheckFollowStatus,
  checkMutualFollowStatus,
  getFollowingWithDetails,
  getFollowersWithDetails,
  getFollowing,
  getFollowers,
  unfollowUser,
  followUser,
  getMultipleUsersByIds
} from "@/lib/appwrite/api";
import { INewPost, IUpdatePost, IUpdateUser } from "@/types";
import { useEffect, useState } from "react";
import { account, appwriteConfig, databases } from "../appwrite/config";
import { ID, Query } from "appwrite";
import { getLiveStreams, getUpcomingLives } from "@/services/liveStreamService";

// ============================================================
// AUTH QUERIES
// ============================================================

const BUSICONN_AGENT_ID = "68575f4e000617f8b3f6"; 

export const useCreateUserAccount = () => {
  return useMutation({
    mutationFn: async (user: {
      name: string;
      username: string;
      emailOrPhone: string;
      password: string;
      acceptedSLA: boolean;
    }) => {
       // Always clear sessions first
       await deleteAllSessions();

      const isEmail = user.emailOrPhone.includes('@');
      const userId = ID.unique();
      try {
        if (isEmail) {
          // Create account with email
          const newAccount = await account.create(
            userId,
            user.emailOrPhone,
            user.password,
            user.name
          );
          if(!newAccount) throw new Error('Account Creation failed');

          // 2. Create verification request
          try {
            await account.createVerification(
              `${window.location.origin}/verify`
            );
          } catch (verificationError) {
            console.error("Verification creation error:", verificationError);
            // Continue even if verification fails
          }

          const session = await account.createEmailPasswordSession(
            user.emailOrPhone,
            user.password
          );

          return session;
        } else {
          // Create phone session first
          const formattedPhone = user.emailOrPhone.startsWith('+91') 
            ? user.emailOrPhone 
            : `+91${user.emailOrPhone}`;

          //const tempEmail = `${formattedPhone.replace('+', '')}@busiconn.com`;  //removed + from this, check whether this will impact or not
          const phoneSession = await account.createPhoneToken(
            userId,
            formattedPhone
          );
          

          return {
            user,
            phoneSession
          };
        }
      } catch (error) {
        console.error('Account creation error:', error);
        throw error;
      }
    }
  });
};

// You might also want to separate the sign-in functionality
export const useSignInAccount = () => {
  return useMutation({
    mutationFn: async ({ 
      emailOrPhone, 
      password 
    }: { 
      emailOrPhone: string; 
      password: string; 
    }) => {
      try {
        // First, delete any existing sessions
        try {
          await deleteAllSessions();
        } catch (error) {
          console.log("No existing sessions to clear");
        }

        const isEmail = emailOrPhone.includes('@');
        
        if (isEmail) {
          return await account.createEmailPasswordSession(emailOrPhone, password);
        } else {
          const formattedPhone = emailOrPhone.startsWith('+91') 
            ? emailOrPhone 
            : `+91${emailOrPhone}`;
          // 1. First try to get the user by phone number
          const phoneEmail = `${formattedPhone}@busiconn.com`;
          try {
            // Create email session with the phone-based email
            const session = await account.createEmailPasswordSession(
              phoneEmail,
              password
            );
            
            return {
              ...session,
              isPhoneUser: true,
              phoneNumber: emailOrPhone
            };
          } catch (error) {
            throw new Error("Invalid phone number or password");
          }
        }
      } catch (error) {
        console.error('Sign in error:', error);
        throw error;
      }
    }
  });
};

export const useVerifyEmail = () => {
  return useMutation({
      mutationFn: async ({ 
          userId, 
          secret 
      }: { 
          userId: string; 
          secret: string; 
      }) => {
          const response = await account.updateVerification(userId, secret);
          return response;
      }
  });
};

export const useVerifyPhone = () => {
  return useMutation({
      mutationFn: async ({ 
          userId, 
          secret 
      }: { 
          userId: string; 
          secret: string; 
      }) => {
          const response = await account.updatePhoneVerification(userId, secret);
          return response;
      }
  });
};

export const useForgotPassword = () => {
  return useMutation({
      mutationFn: async (email: string) => {
          const response = await account.createRecovery(
              email,
              'http://localhost:5173/reset-password'
          );
          return response;
      }
  });
};

export const useResetPassword = () => {
  return useMutation({
      mutationFn: async ({ 
          userId, 
          secret, 
          password 
      }: { 
          userId: string; 
          secret: string; 
          password: string; 
      }) => {
          const response = await account.updateRecovery(
              userId,
              secret,
              password
          );
          return response;
      }
  });
};

export const useSignOutAccount = () => {
  return useMutation({
    mutationFn: signOutAccount,
  });
};

// ============================================================
// POST QUERIES
// ============================================================

export const useGetPosts = () => {
  return useInfiniteQuery({
    queryKey: [QUERY_KEYS.GET_INFINITE_POSTS],
    queryFn: getInfinitePosts as any,
    getNextPageParam: (lastPage: any) => {
      // If there's no data, there are no more pages.
      if (lastPage && lastPage.documents.length === 0) {
        return null;
      }

      // Use the $id of the last document as the cursor.
      const lastId = lastPage.documents[lastPage.documents.length - 1].$id;
      return lastId;
    },
  });
};

export const useSearchPosts = (searchTerm: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.SEARCH_POSTS, searchTerm],
    queryFn: () => searchPosts(searchTerm),
    enabled: !!searchTerm,
  });
};

export const useGetRecentPosts = (
  category?: string,
  individual?: string,
  isActivated?: boolean
) => {
  // Build filter object dynamically, ensuring no `undefined` values
  const filterBy: Record<string, string | boolean> = {};
  
  if (category && category !== "all") {
    filterBy.category = category;
  }
  if (individual) {
    filterBy.individual = individual;
  }
  if (typeof isActivated === "boolean") {
    filterBy.isActivated = isActivated;
  }

  return useInfiniteQuery({
    queryKey: [QUERY_KEYS.GET_RECENT_POSTS, filterBy],
    queryFn: ({ pageParam = 0 }) => getRecentPosts({ pageParam, filterBy }),
    getNextPageParam: (lastPage, pages) =>
      lastPage.documents.length < 5 ? undefined : pages.length,
  });
};

// export const useGetRecentPostsWithFilter = (filterBy = '') => {
//   return useInfiniteQuery({
//     queryKey: [QUERY_KEYS.GET_RECENT_POSTS, filterBy],
//     queryFn: ({ pageParam = 0 }) => getRecentPostsWithFilter({ pageParam, filterBy }),
//     getNextPageParam: (lastPage, pages) => {
//       return lastPage.documents.length < 5 ? undefined : pages.length;
//     },
//   });
// };

export const useCreatePost = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (post: INewPost) => createPost(post),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_RECENT_POSTS],
      });
    },
  });
};

export const useGetPostById = (postId?: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_POST_BY_ID, postId],
    queryFn: () => getPostById(postId),
    enabled: !!postId,
  });
};

export const useGetUserPosts = (userId: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_USER_POSTS, userId],
    queryFn: async () => {
      if (!userId) return { documents: [] };
      
      const posts = await databases.listDocuments(
        appwriteConfig.databaseId,
        appwriteConfig.postCollectionId,
        [
          Query.equal("creatorId", userId),
          Query.orderDesc("$createdAt"),
          Query.limit(50)
        ]
      );
      
      return posts;
    },
    enabled: !!userId,
  });
};

export const useUpdatePost = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (post: IUpdatePost) => updatePost(post),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_POST_BY_ID, data?.$id],
      });
    },
  });
};

export const useDeletePost = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ postId, imageId }: { postId?: string; imageId: string }) =>
      deletePost(postId, imageId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_RECENT_POSTS],
      });
    },
  });
};

export const useLikePost = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      postId,
      likesArray,
    }: {
      postId: string;
      likesArray: string[];
    }) => likePost(postId, likesArray),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_POST_BY_ID, data?.$id],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_RECENT_POSTS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_POSTS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_CURRENT_USER],
      });
    },
  });
};

export const useSavePost = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ 
      postId, 
      savedByArray
    }: { 
      postId: string; 
      savedByArray: string[];
    }) => savePost(postId, savedByArray),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_RECENT_POSTS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_POSTS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_CURRENT_USER],
      });
    },
  });
};

export const useDeleteSavedPost = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (savedRecordId: string) => deleteSavedPost(savedRecordId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_RECENT_POSTS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_POSTS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_CURRENT_USER],
      });
    },
  });
};

// ============================================================
// USER QUERIES
// ============================================================

export const useGetCurrentUser = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_CURRENT_USER],
    queryFn: getCurrentUser,
  });
};

export const useGetUsers = (limit?: number) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_USERS],
    queryFn: () => getUsers(limit),
  });
};

export const useGetStreams = (limit?: number) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_LIVE_STREAMS],
    queryFn: () => getLiveStreams(limit),
  });
};


export const useGetUserById = (userId: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_USER_BY_ID, userId],
    queryFn: () => getUserById(userId),
    enabled: !!userId,
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (user: IUpdateUser) => updateUser(user),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_CURRENT_USER],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_USER_BY_ID, data?.$id],
      });
    },
  });
};

// ============================================================
// Sender Reciever Chat System
// ============================================================

export const useFetchMessages = (senderId: string, recipientId: string, currentUserId?: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_MESSAGES, senderId, recipientId, currentUserId],
    queryFn: () => fetchMessages(senderId, recipientId, currentUserId || senderId),
    enabled: !!senderId && !!recipientId, // Only fetch if both IDs are present
  });
};

export const useSendMessage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messageData: { 
      senderId: string; 
      recipientId: string; 
      message: string; 
      attachments?: File[];
      isAgentMessage?: boolean;
    }) => sendMessage(messageData),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MESSAGES] }),
    onError: (error) => console.error("Send failed:", error)
  });
};

// Update your fetchRelatedUsers to show conversation pairs for agent
const fetchRelatedUsers = async (senderId: string, isAgent = false) => {
  try {
    const messages = await fetchMessagesForUser(senderId, isAgent);
    if (!messages || !Array.isArray(messages.documents)) return [];
    
    if (isAgent) {
      // For agent: group conversations by pairs, not individual users
      const conversationPairs = new Map<string, { user1: string, user2: string, lastMessage: any }>();
      
      messages.documents.forEach((msg) => {
        // Skip messages involving the agent
        if (msg.sender_id === BUSICONN_AGENT_ID || msg.recipient_id === BUSICONN_AGENT_ID) {
          return;
        }
        
        // Create conversation key (always put smaller ID first for consistency)
        const participants = [msg.sender_id, msg.recipient_id].sort();
        const conversationKey = participants.join('-');
        
        // Keep track of the latest message for each conversation
        if (!conversationPairs.has(conversationKey) || 
            new Date(msg.$createdAt) > new Date(conversationPairs.get(conversationKey)?.lastMessage?.$createdAt || 0)) {
          conversationPairs.set(conversationKey, {
            user1: participants[0],
            user2: participants[1],
            lastMessage: msg
          });
        }
      });
      
      // Convert conversation pairs to user objects that represent the conversation
      const conversationUsers = await Promise.all(
        Array.from(conversationPairs.values()).map(async (pair) => {
          try {
            const [user1, user2] = await Promise.all([
              databases.getDocument(appwriteConfig.databaseId, appwriteConfig.userCollectionId, pair.user1),
              databases.getDocument(appwriteConfig.databaseId, appwriteConfig.userCollectionId, pair.user2)
            ]);
            
            // Return a combined user object that represents the conversation
            return {
              $id: `${pair.user1}-${pair.user2}`, // Special ID for conversation
              name: `${user1.name} ↔ ${user2.name}`, // Show both names
              username: `conversation-${pair.user1}-${pair.user2}`,
              emailOrPhone: '',
              imageUrl: '', // We'll handle avatar differently
              bio: `Conversation between ${user1.name} and ${user2.name}`,
              acceptedSLA: true,
              isConversation: true, // Flag to identify this as a conversation pair
              participants: [user1, user2], // Store the actual user objects
              lastMessage: pair.lastMessage
            };
          } catch (error) {
            console.error('Error fetching conversation participants:', error);
            return null;
          }
        })
      );
      
      return conversationUsers.filter(conv => conv !== null);
    } else {
      // Regular user logic remains the same
      const userIds = Array.from(new Set(
        messages.documents.flatMap((msg) => 
          [msg.sender_id, msg.recipient_id].filter((id) => id !== senderId)
        )
      ));
      
      return await fetchUsersByIds(userIds);
    }
  } catch (error) {
    console.error('Error fetching related users:', error);
    return [];
  }
};

export const useGetRelatedUsers = (senderId: string, isAgent?: boolean) => {
  return useQuery({
    queryKey: ["relatedUsers", senderId, isAgent],
    queryFn: () => fetchRelatedUsers(senderId, isAgent),
    enabled: !!senderId,
  });
};


export const useLastMessages = (currentUserId: string | null) => {
  const [lastMessages, setLastMessages] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchMessages = async () => {
      if (!currentUserId) return;
      
      setIsLoading(true);
      try {
        const messages = await getLastMessages(currentUserId);
        setLastMessages(messages);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Unknown error'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchMessages();
  }, [currentUserId]);

  return { lastMessages, isLoading, error };
};

export const useFollowUser = (options?: {
  onSuccess?: () => void;
  onError?: (error: any) => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: followUser,
    onMutate: async ({ followerId, followingId }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ 
        queryKey: ["followStatus", followerId, followingId] 
      });
      await queryClient.cancelQueries({ 
        queryKey: ["followersCount", followingId] 
      });

      // Snapshot previous values
      const previousFollowStatus = queryClient.getQueryData([
        "followStatus", 
        followerId, 
        followingId
      ]);
      const previousFollowersCount = queryClient.getQueryData([
        "followersCount", 
        followingId
      ]);

      // Optimistically update follow status
      queryClient.setQueryData(
        ["followStatus", followerId, followingId],
        { $id: "temp-optimistic-id", followerId, followingId }
      );

      // Optimistically update followers count
      queryClient.setQueryData(
        ["followersCount", followingId],
        (old: number | undefined) => (old || 0) + 1
      );

      return { previousFollowStatus, previousFollowersCount };
    },
    onError: (error, variables, context) => {
      // Revert optimistic updates on error
      if (context?.previousFollowStatus !== undefined) {
        queryClient.setQueryData(
          ["followStatus", variables.followerId, variables.followingId],
          context.previousFollowStatus
        );
      }
      if (context?.previousFollowersCount !== undefined) {
        queryClient.setQueryData(
          ["followersCount", variables.followingId],
          context.previousFollowersCount
        );
      }
      
      console.error("Follow user error:", error);
      options?.onError?.(error);
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch related queries
      queryClient.invalidateQueries({ 
        queryKey: ["followStatus", variables.followerId, variables.followingId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ["followersCount", variables.followingId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ["followingCount", variables.followerId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ["followers", variables.followingId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ["following", variables.followerId] 
      });

      // Update cache with real data
      queryClient.setQueryData(
        ["followStatus", variables.followerId, variables.followingId],
        data
      );

      options?.onSuccess?.();
    },
  });
};

// Enhanced useUnfollowUser hook with optimistic updates
export const useUnfollowUser = (options?: {
  onSuccess?: () => void;
  onError?: (error: any) => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: unfollowUser,
    onMutate: async (followId: string) => {
      // Find the follow relationship to get user IDs
      const followStatusQueries = queryClient.getQueriesData({ 
        queryKey: ["followStatus"] 
      });

      let followerId: string | undefined;
      let followingId: string | undefined;

      // Find the relevant follow status query
      followStatusQueries.forEach(([queryKey, queryData]) => {
        if (queryData && typeof queryData === 'object' && '$id' in queryData && queryData.$id === followId) {
          const [, , fId, fgId] = queryKey as string[];
          followerId = fId;
          followingId = fgId;
        }
      });

      if (!followerId || !followingId) return;

      // Cancel outgoing refetches
      await queryClient.cancelQueries({ 
        queryKey: ["followStatus", followerId, followingId] 
      });
      await queryClient.cancelQueries({ 
        queryKey: ["followersCount", followingId] 
      });

      // Snapshot previous values
      const previousFollowStatus = queryClient.getQueryData([
        "followStatus", 
        followerId, 
        followingId
      ]);
      const previousFollowersCount = queryClient.getQueryData([
        "followersCount", 
        followingId
      ]);

      // Optimistically update
      queryClient.setQueryData(
        ["followStatus", followerId, followingId],
        null
      );
      queryClient.setQueryData(
        ["followersCount", followingId],
        (old: number | undefined) => Math.max((old || 1) - 1, 0)
      );

      return { previousFollowStatus, previousFollowersCount, followerId, followingId };
    },
    onError: (error, _followId, context) => {
      // Revert optimistic updates on error
      if (context?.followerId && context?.followingId) {
        if (context.previousFollowStatus !== undefined) {
          queryClient.setQueryData(
            ["followStatus", context.followerId, context.followingId],
            context.previousFollowStatus
          );
        }
        if (context.previousFollowersCount !== undefined) {
          queryClient.setQueryData(
            ["followersCount", context.followingId],
            context.previousFollowersCount
          );
        }
      }
      
      console.error("Unfollow user error:", error);
      options?.onError?.(error);
    },
    onSuccess: (_data, _followId, context) => {
      if (context?.followerId && context?.followingId) {
        // Invalidate related queries
        queryClient.invalidateQueries({ 
          queryKey: ["followStatus", context.followerId, context.followingId] 
        });
        queryClient.invalidateQueries({ 
          queryKey: ["followersCount", context.followingId] 
        });
        queryClient.invalidateQueries({ 
          queryKey: ["followingCount", context.followerId] 
        });
        queryClient.invalidateQueries({ 
          queryKey: ["followers", context.followingId] 
        });
        queryClient.invalidateQueries({ 
          queryKey: ["following", context.followerId] 
        });
      }

      options?.onSuccess?.();
    },
  });
};

// Enhanced useCheckFollowStatus hook
export const useCheckFollowStatus = (followerId: string, followingId: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_FOLLOW_STATUS, followerId, followingId],
    queryFn: () => checkFollowStatus(followerId, followingId),
    enabled: !!followerId && !!followingId && followerId !== followingId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 10, // 10 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });
};

// Enhanced followers count hook
export const useGetFollowersCount = (userId: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_FOLLOWERS_COUNT, userId],
    queryFn: () => getFollowersCount(userId),
    enabled: !!userId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    cacheTime: 1000 * 60 * 5,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Enhanced following count hook
export const useGetFollowingCount = (userId: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_FOLLOWING_COUNT, userId],
    queryFn: () => getFollowingCount(userId),
    enabled: !!userId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    cacheTime: 1000 * 60 * 5,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Get followers list (basic)
export const useGetFollowers = (userId: string, page: number = 1, pageSize: number = 20) => {
  return useQuery({
    queryKey: ["followers", userId, page, pageSize],
    queryFn: () => getFollowers(userId, page, pageSize),
    enabled: !!userId,
    staleTime: 1000 * 60 * 3, // 3 minutes
    keepPreviousData: true, // For pagination
  });
};

// Get following list (basic)
export const useGetFollowing = (userId: string, page: number = 1, pageSize: number = 20) => {
  return useQuery({
    queryKey: ["following", userId, page, pageSize],
    queryFn: () => getFollowing(userId, page, pageSize),
    enabled: !!userId,
    staleTime: 1000 * 60 * 3, // 3 minutes
    keepPreviousData: true, // For pagination
  });
};

// Get followers with user details
export const useGetFollowersWithDetails = (userId: string, page: number = 1, pageSize: number = 20) => {
  return useQuery({
    queryKey: ["followersWithDetails", userId, page, pageSize],
    queryFn: () => getFollowersWithDetails(userId, page, pageSize),
    enabled: !!userId,
    staleTime: 1000 * 60 * 3, // 3 minutes
    keepPreviousData: true,
  });
};

// Get following with user details
export const useGetFollowingWithDetails = (userId: string, page: number = 1, pageSize: number = 20) => {
  return useQuery({
    queryKey: ["followingWithDetails", userId, page, pageSize],
    queryFn: () => getFollowingWithDetails(userId, page, pageSize),
    enabled: !!userId,
    staleTime: 1000 * 60 * 3, // 3 minutes
    keepPreviousData: true,
  });
};

// Check mutual follow status
export const useCheckMutualFollowStatus = (userId1: string, userId2: string) => {
  return useQuery({
    queryKey: ["mutualFollowStatus", userId1, userId2],
    queryFn: () => checkMutualFollowStatus(userId1, userId2),
    enabled: !!userId1 && !!userId2 && userId1 !== userId2,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

// Bulk follow status check
export const useBulkCheckFollowStatus = (followerId: string, userIds: string[]) => {
  return useQuery({
    queryKey: ["bulkFollowStatus", followerId, userIds],
    queryFn: () => bulkCheckFollowStatus(followerId, userIds),
    enabled: !!followerId && userIds.length > 0,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

// Utility hook to prefetch follow-related data
export const usePrefetchFollowData = () => {
  const queryClient = useQueryClient();

  const prefetchFollowData = (userId: string, currentUserId: string) => {
    // Prefetch follow status
    queryClient.prefetchQuery({
      queryKey: ["followStatus", currentUserId, userId],
      queryFn: () => checkFollowStatus(currentUserId, userId),
      staleTime: 1000 * 60 * 5,
    });

    // Prefetch followers count
    queryClient.prefetchQuery({
      queryKey: ["followersCount", userId],
      queryFn: () => getFollowersCount(userId),
      staleTime: 1000 * 60 * 2,
    });

    // Prefetch following count
    queryClient.prefetchQuery({
      queryKey: ["followingCount", userId],
      queryFn: () => getFollowingCount(userId),
      staleTime: 1000 * 60 * 2,
    });
  };

  return { prefetchFollowData };
};

export const useGetUpcomingLives = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_UPCOMING_LIVES], // Remove sellerId from queryKey
    queryFn: () => getUpcomingLives(), // Remove sellerId parameter
    // Remove enabled condition since we don't need sellerId anymore
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useGetMultipleUsersByIds = (userIds: string[]) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_MULTIPLE_USERS, userIds.sort()], // Sort for consistent cache key
    queryFn: () => getMultipleUsersByIds(userIds),
    enabled: !!userIds && userIds.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
};